import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    getMusicVolume, getSfxVolume, setMusicVolume, setSfxVolume, primeAudio, playUi,
    DEFAULT_MUSIC, DEFAULT_SFX,
} from '../../utils/chiptune';

const SpeakerIcon = ({ muted }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={muted ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.75)'}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        {muted ? (
            <>
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
            </>
        ) : (
            <>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </>
        )}
    </svg>
);

export default function AudioSettings({ style }) {
    const [open, setOpen] = useState(false);
    const [music, setMusic] = useState(getMusicVolume);
    const [sfx, setSfx] = useState(getSfxVolume);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', away);
        return () => document.removeEventListener('pointerdown', away);
    }, [open]);

    const onMusic = useCallback((v) => {
        const n = parseFloat(v);
        primeAudio();
        setMusicVolume(n);
        setMusic(n);
    }, []);

    const onSfx = useCallback((v) => {
        const n = parseFloat(v);
        primeAudio();
        setSfxVolume(n);
        setSfx(n);
        if (n > 0) playUi();
    }, []);

    const allMuted = music === 0 && sfx === 0;

    const toggleAll = useCallback(() => {
        primeAudio();
        if (allMuted) {
            setMusicVolume(DEFAULT_MUSIC); setMusic(DEFAULT_MUSIC);
            setSfxVolume(DEFAULT_SFX); setSfx(DEFAULT_SFX);
            playUi();
        } else {
            setMusicVolume(0); setMusic(0);
            setSfxVolume(0); setSfx(0);
        }
    }, [allMuted]);

    const rows = [
        { label: 'Sound FX', val: sfx, onChange: onSfx },
        { label: 'Music', val: music, onChange: onMusic },
    ];

    return (
        <div
            ref={wrapRef}
            style={{
                position: 'relative', display: 'inline-flex',
                fontFamily: "'Jersey 10', monospace",
                ...style,
            }}
        >
            <button
                onClick={() => { primeAudio(); setOpen(o => !o); }}
                title="Audio settings"
                aria-label="Audio settings"
                style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '3px 9px', borderRadius: 4, cursor: 'pointer',
                    background: open ? 'rgba(77,255,136,0.18)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${open ? 'rgba(77,255,136,0.45)' : 'rgba(255,255,255,0.1)'}`,
                }}
            >
                <SpeakerIcon muted={allMuted} />
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 20,
                    background: 'rgba(8,7,20,0.94)',
                    border: '1px solid rgba(77,255,136,0.28)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    backdropFilter: 'blur(8px)',
                    width: 190,
                    maxWidth: 'calc(100vw - 32px)',
                    display: 'flex', flexDirection: 'column', gap: 10,
                    boxShadow: '0 8px 26px rgba(0,0,0,0.6)',
                }}>
                    {rows.map(({ label, val, onChange }) => (
                        <div key={label}>
                            <div style={{
                                color: 'rgba(255,255,255,0.45)', fontSize: 15,
                                letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 3,
                            }}>
                                {label}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="range" min="0" max="1" step="0.05"
                                    value={val}
                                    onChange={e => onChange(e.target.value)}
                                    style={{ flex: 1, accentColor: '#4dff88', cursor: 'pointer', height: 3, minWidth: 90 }}
                                />
                                <span className="tabular-nums" style={{
                                    color: 'rgba(255,255,255,0.6)', fontSize: 15, minWidth: 32, textAlign: 'right',
                                }}>
                                    {Math.round(val * 100)}%
                                </span>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={toggleAll}
                        style={{
                            padding: '3px 0', borderRadius: 5, cursor: 'pointer',
                            background: allMuted ? 'rgba(77,255,136,0.16)' : 'rgba(255,255,255,0.06)',
                            border: `1px solid ${allMuted ? 'rgba(77,255,136,0.4)' : 'rgba(255,255,255,0.12)'}`,
                            color: allMuted ? '#8bffb0' : 'rgba(255,255,255,0.5)',
                            fontFamily: 'inherit', fontSize: 16,
                            letterSpacing: '0.12em', textTransform: 'uppercase',
                        }}
                    >
                        {allMuted ? 'Unmute all' : 'Mute all'}
                    </button>
                </div>
            )}
        </div>
    );
}
